import { CommonModule } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { CommonService } from '../../../services/common/common.service';

type NavItem = {
  name: string;
  icon: string; // icon KEY (e.g. 'dashboard'), rendered by the #navIcon template in the HTML
  path?: string;
  new?: boolean;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

interface NestedSubItem {
  name: string;
  path?: string;
  pro?: boolean;
  new?: boolean;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
}

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    TooltipModule
  ],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  isExpanded = true;
  isMobileOpen = false;

  private menuPermissionMap: Record<string, string[]> = {
    'Document Tracker': ['VIEW_DOCUMENT_TRACKER_MENU'],
    'Map Profile': ['VIEW_MAP_LIBRARY_MENU', 'VIEW_MAP_ERROR_LIST_MENU', 'VIEW_CONVERSION_CODE_LIST'],
    'Map Library': ['VIEW_MAP_LIBRARY_MENU'],
    'Map Error List': ['VIEW_MAP_ERROR_LIST_MENU'],
    'Conversion Code List': ['VIEW_CONVERSION_CODE_LIST_MENU'],
    'File Profile': ['VIEW_CUSTOM_FILE_DEFINITIONS_LIST', 'VIEW_STANDARD_FILE_DEFINITION_MENU'],
    'Custom File Definition': ['VIEW_CUSTOM_FILE_DEFINITION_MENU'],
    'Standard File Definition': ['VIEW_STANDARD_FILE_DEFINITION_MENU'],
    'Partners': ['VIEW_PARTNER_SETUP_MENU', 'VIEW_PARTNER_CONNECTIVITY_SETUP_MENU', 'VIEW_PARTNER_CONTRACT_MENU'],
    'Partner Setup': ['VIEW_PARTNER_SETUP_MENU'],
    'Partner Connectivity': ['VIEW_PARTNER_CONNECTIVITY_SETUP_MENU'],
    'Partner Contract': ['VIEW_PARTNER_CONTRACT_MENU'],
    'Business Process': ['VIEW_BUSINESS_PROCESS_MENU'],
    'Payment Terms': ['VIEW_PAYMENT_TERMS_MENU'],
    'Status Management': ['VIEW_STATUS_MANAGEMENT_MENU'],
    'Regex Management': ['VIEW_REGEX_MANAGEMENT_MENU'],
    'Announcement Management': ['VIEW_ANNOUNCEMENT_MENU'],
    'User Management': ['VIEW_USER_MANAGEMENT_MENU'],
    'Role Management': ['VIEW_ROLES_MANAGEMENT_MENU'],
    'Column Settings': ['VIEW_COLUMN_SETTING_MENU'],
    'Email Template': ['VIEW_EMAIL_TEMPLATE_MENU'],
    'User Activity Log': ['VIEW_USER_ACTIVITY_MENU'],
    'Customer Management': ['VIEW_CUSTOMER_MENU'],
    'Developer': ['VIEW_DEVELOPER_MENU'],
    'New Migration': ['MIGRATION_NEW_REQUEST'],
    'My Requests': ['MIGRATION_MY_REQUESTS'],
    'Approvals': ['MIGRATION_APPROVALS'],
    'Documentation': ['VIEW_API_DOCUMENTATION_MENU'],
    'API Clients': ['VIEW_DEVELOPER_CLIENTS_MENU'],
  };

  // Menu data is filled by buildMenus()
  topLevelItems: NavItem[] = [];
  mapItems: NavItem[] = [];
  fileDefinitionItems: NavItem[] = [];
  partnerManagementItems: NavItem[] = [];
  configurationItems: NavItem[] = [];
  administrationItems: NavItem[] = [];
  migrationItems: NavItem[] = [];
  developerItems: NavItem[] = [];

  masterDataItems: NavItem[] = [];
  superAdmin: boolean = false;
  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  openNestedSubmenu: string | null = null;
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;

  private subscription: Subscription = new Subscription();
  privileges: any[] = [];

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    this.superAdmin = userDetails?.is_superuser;
    this.privileges = userDetails?.privileges || [];
    this.buildMenus(userDetails);
  }

  // All menu data lives here once. Icons are KEYS, not HTML.
  private buildMenus(userDetails: any): void {
    this.topLevelItems = [
      { icon: 'dashboard', name: 'Dashboard', path: '/' },
      { icon: 'document-tracker', name: 'Document Tracker', path: '/document-tracker' },
    ];

    this.mapItems = [
      {
        name: 'Map Profile',
        icon: 'map-profile',
        subItems: [
          { name: 'Map Library', path: '/map/library' },
          { name: 'Map Error List', path: '/map/map-error-code-list' },
          { name: 'Conversion Code List', path: '/map/code-list' },
        ],
      },
    ];

    this.fileDefinitionItems = [
      {
        name: 'File Profile',
        icon: 'file-profile',
        subItems: [
          {
            name: 'Custom File Definition',
            subItems: [
              { name: 'JSON', path: '/file-profile/custom/json' },
              { name: 'XML', path: '/file-profile/custom/xml' },
              { name: 'PFF', path: '/file-profile/custom/pff' },
              // { name: 'CSV', path: '/file-profile/custom/csv' },
            ],
          } as any,
          { name: 'Standard File Definition', path: '/file-profile/standard' },
        ],
      },
    ];

    this.partnerManagementItems = [
      {
        name: 'Partners',
        icon: 'partners',
        subItems: [
          { name: 'Partner Setup', path: '/partners/setup' },
          { name: 'Partner Connectivity', path: '/partners/connectivity' },
          { name: 'Partner Contract', path: '/partners/contract' },
        ],
      },
    ];

    this.configurationItems = [
      { icon: 'business-process', name: 'Business Process', path: '/master-data/business-process' },
      { icon: 'payment-terms', name: 'Payment Terms', path: '/master-data/payment-terms' },
      { icon: 'status-management', name: 'Status Management', path: '/master-data/status-management' },
      { icon: 'regex', name: 'Regex Management', path: '/master-data/regex-management' },
      { icon: 'announcement', name: 'Announcement Management', path: '/master-data/announcement-management' },
      { icon: 'column-settings', name: 'Column Settings', path: '/master-data/column-settings' },
      { icon: 'email-template', name: 'Email Template', path: '/master-data/email-template' },
    ];

    this.administrationItems = [
      { icon: 'user-management', name: 'User Management', path: '/master-data/users' },
      { icon: 'role', name: 'Role Management', path: '/master-data/role' },
      { icon: 'activity-log', name: 'User Activity Log', path: '/master-data/user-activity-log' },
    ];

    this.migrationItems = [
      { icon: 'migration-new', name: 'New Migration', path: '/migrate-data/new' },
      { icon: 'migration-requests', name: 'My Requests', path: '/migrate-data/my-requests' },
      { icon: 'migration-approvals', name: 'Approvals', path: '/migrate-data/approvals' },
    ];

    if (this.superAdmin || userDetails?.role_name === 'Super Admin') {
      this.administrationItems.push({
        icon: 'customer',
        name: 'Customer Management',
        path: '/master-data/customer',
      });
    }

    this.developerItems = [
      { icon: 'api-clients', name: 'API Clients', path: '/developer/clients' },
      { icon: 'documentation', name: 'Documentation', path: '/developer/documentation' },
    ];
  }

  isSuperAdmin(): boolean {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const roleName =
      userDetails?.role_name ||
      userDetails?.role_data?.[0]?.role_name ||
      '';
    return roleName === 'Super Admin';
  }

  ngOnInit() {
    this.filterAllMenus();
    this.subscription.add(
      this.commonService.privilegesUpdated$.subscribe(() => {
        this.reloadMenus();
      })
    );
    this.subscription.add(
      this.isExpanded$.subscribe((value) => {
        this.isExpanded = value;
      })
    );
    this.subscription.add(
      this.isMobileOpen$.subscribe((value) => {
        this.isMobileOpen = value;
      })
    );
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    this.setActiveMenuFromRoute(this.router.url);
  }

  private filterAllMenus(): void {
    this.topLevelItems = this.filterByPrivilege([...this.topLevelItems], 'top');
    this.mapItems = this.filterByPrivilege([...this.mapItems], 'map');
    this.fileDefinitionItems = this.filterByPrivilege([...this.fileDefinitionItems], 'file');
    this.partnerManagementItems = this.filterByPrivilege([...this.partnerManagementItems], 'partner');
    this.configurationItems = this.filterByPrivilege([...this.configurationItems], 'config');
    this.administrationItems = this.filterByPrivilege([...this.administrationItems], 'admin');
    this.migrationItems = this.filterByPrivilege([...this.migrationItems], 'migration');
    this.developerItems = this.filterByPrivilege([...this.developerItems], 'dev');
  }

  private filterByPrivilege(items: NavItem[], section: string = ''): NavItem[] {
    return items.filter(item => {
      const allowed = this.canAccess(item.name);
      if (!allowed) return false;
      if (item.subItems) {
        item.subItems = item.subItems.filter((sub: any) => {
          const subAllowed = this.canAccess(sub.name);
          if (!subAllowed) return false;
          if (sub.subItems) {
            sub.subItems = sub.subItems.filter((nested: any) => this.canAccess(nested.name));
            return sub.subItems.length > 0;
          }
          return true;
        });
        return item.subItems.length > 0;
      }
      return true;
    });
  }

  private canAccess(itemName: string): boolean {
    const requiredPrivileges = this.menuPermissionMap[itemName];
    if (!requiredPrivileges) return true;
    return requiredPrivileges.some(rp => this.privileges.includes(rp));
  }

  private reloadMenus(): void {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    this.superAdmin = userDetails?.is_superuser;
    this.privileges = userDetails?.privileges || [];

    // Rebuild the original menus (filtering mutates them), then filter again
    this.buildMenus(userDetails);
    this.filterAllMenus();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  toggleSubmenu(level: 'main' | 'nested', key: string) {
    if (level === 'main') {
      this.openSubmenu = this.openSubmenu === key ? null : key;
    } else {
      this.openNestedSubmenu = this.openNestedSubmenu === key ? null : key;
    }

    setTimeout(() => {
      const el = document.getElementById(key);
      if (el) {
        this.subMenuHeights[key] = el.scrollHeight;
        this.cdr.detectChanges();
      }
    });
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    const menuGroups = [
      { items: this.topLevelItems, prefix: 'top' },
      { items: this.mapItems, prefix: 'main' },
      { items: this.fileDefinitionItems, prefix: 'file' },
      { items: this.partnerManagementItems, prefix: 'partner' },
      { items: this.configurationItems, prefix: 'config' },
      { items: this.administrationItems, prefix: 'admin' },
      { items: this.migrationItems, prefix: 'migration' },
      { items: this.developerItems, prefix: 'dev' },
    ];

    let isSubmenuMatch = false;

    for (const group of menuGroups) {
      group.items.forEach((nav, i) => {
        if (this.matchNavItem(nav, i, group.prefix, currentUrl)) {
          isSubmenuMatch = true;
        }
      });
    }

    if (!isSubmenuMatch) {
      this.openSubmenu = null;
    }
  }

  /** Opens the right submenu(s) for one nav item. Returns true if the URL matched. */
  private matchNavItem(nav: NavItem, i: number, prefix: string, currentUrl: string): boolean {
    // Direct path match
    if (nav.path && currentUrl === nav.path) {
      this.openSubmenu = null;
      return true;
    }

    let matched = false;
    (nav.subItems ?? []).forEach((subItem: any, j: number) => {
      if (this.matchSubItem(subItem, i, j, prefix, currentUrl)) {
        matched = true;
      }
    });
    return matched;
  }

  /** Level 1 sub item, then its nested items. Returns true if the URL matched. */
  private matchSubItem(subItem: any, i: number, j: number, prefix: string, currentUrl: string): boolean {
    const level1Key = `${prefix}-${i}`;

    if (subItem.path && currentUrl === subItem.path) {
      this.openSubmenu = level1Key;
      setTimeout(() => this.measureSubMenuHeight(level1Key));
      return true;
    }

    const hit = (subItem.subItems ?? []).some(
      (nested: any) => nested.path && currentUrl === nested.path
    );
    if (!hit) return false;

    const level2Key = `${prefix}-${i}-sub-${j}`;
    this.openSubmenu = level1Key;
    this.openNestedSubmenu = level2Key;
    setTimeout(() => {
      this.measureSubMenuHeight(level1Key);
      this.measureSubMenuHeight(level2Key);
    });
    return true;
  }

  private measureSubMenuHeight(key: string) {
    setTimeout(() => {
      const el = document.getElementById(key);
      if (el) {
        this.subMenuHeights[key] = el.scrollHeight;
        this.cdr.detectChanges();
      }
    }, 50);
  }

  onSubmenuClick(event?: Event) {
    event?.stopPropagation();
  }

  hasNestedSubItems(subItem: any): boolean {
    return subItem?.subItems && Array.isArray(subItem.subItems);
  }

  getNestedSubItems(subItem: any): any[] {
    return subItem?.subItems || [];
  }

  getSubItemName(subItem: any): string {
    return subItem?.name || '';
  }

  getSubItemPath(subItem: any): string | undefined {
    return subItem?.path;
  }

  isSidebarCollapsed(): boolean {
    return !this.isExpanded && !this.isMobileOpen;
  }

  showCollapsedSubmenu(item: any, event: MouseEvent) {
    if (!this.isSidebarCollapsed()) return;
    this.openSubmenu = item.name;
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setTimeout(() => {
      const popup = document.getElementById('collapsed-submenu-popup');
      if (popup) {
        popup.style.top = `${rect.top}px`;
        popup.style.left = `80px`;
      }
    });
  }

  hideCollapsedSubmenu() {
    if (this.isSidebarCollapsed()) {
      this.openSubmenu = null;
      this.openNestedSubmenu = null;
    }
  }
}